import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { Dispatch, SetStateAction, useEffect, useState } from "react";

import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "./CustomSnackBar";
import { CookService } from "../../services/CookServices";

const cookService = new CookService();

interface Props {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  cookId: number;
  minWalletThresholdAmount?: string;
  onSuccess?: () => void;
}

export const UpdateMinWalletThresholdDialog = ({
  open,
  setOpen,
  cookId,
  minWalletThresholdAmount,
  onSuccess
}: Props) => {
  const [threshold, setThreshold] = useState<string>("");
  const [minWalletBalance, setMinWalletBalance] = useState<string>("");

  useEffect(() => {
    if (open) {
      setThreshold(minWalletThresholdAmount ?? "");
    }
  }, [open, minWalletThresholdAmount]);

  const handleClose = () => {
    setOpen(false);
    setThreshold("");
    setMinWalletBalance("");
  };

  const handleSubmit = async () => {
    try {
      await cookService.updateMinWalletThreshold(cookId, {
        minWalletThresholdAmount: threshold, minWalletBalance
      });

      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Wallet limits updated successfully",
        type: CustomSnackbarTypes.SUCCESS
      });

      onSuccess?.();
      setOpen(false);
      setThreshold("");
      setMinWalletBalance("");
      window.location.reload();
    } catch (error) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Failed to update wallet limits",
        type: CustomSnackbarTypes.ERROR
      });
    }
  };

  const isDisabled =
    threshold.trim() === "" &&
    minWalletBalance.trim() === "";

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        Update Wallet Withdrawal Limits
        <IconButton
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 2, fontSize: "0.78rem" }}>
          <strong>Cook</strong> can withdraw when balance &gt; Cook Minimum Withdrawal Limit (down to Agent Minimum Withdrawal Limit).<br />
          <strong>Agent</strong> can withdraw between Cook Minimum Withdrawal Limit and Agent Minimum Withdrawal Limit.<br />
          <strong>Admin</strong> can withdraw below Agent Minimum Withdrawal Limit.
        </Alert>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Cook Minimum Withdrawal Limit"
              fullWidth
              type="number"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              placeholder="Enter amount"
              helperText="Cook can initiate a withdrawal when balance is above this limit"
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Agent Minimum Withdrawal Limit"
              fullWidth
              type="number"
              value={minWalletBalance}
              onChange={(e) => setMinWalletBalance(e.target.value)}
              placeholder="Enter amount"
              helperText="Agents can withdraw down to this limit — only admins can withdraw below it"
              inputProps={{ min: 0 }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          color="success"
          onClick={handleSubmit}
          disabled={isDisabled}
        >
          Update
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleClose}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};
